import classNames from "classnames";
import { add } from "date-fns";
import { useState } from "react";
import type { Agent } from "../../common/types/Agent";
import "../../css/Tooltip.css";
import "./Mentions.css";
import { agentNames } from "../../utils/name.util";
import { AgentAvatar, AgentAvatarList } from "../Common/Avatar";
import HandleClicksOutside from "../HandleClicksOutside";
import "./MessageInput.css";
import {
    Combobox,
    ComboboxChip,
    ComboboxChips,
    ComboboxChipsInput,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxItem,
    ComboboxList,
    useComboboxAnchor,
} from "@/components/ui/combobox";
import Button from "../Common/Buttons";
import { FormattedDateTime } from "../Common/DateTime";
import { Flex } from "../Common/Flex";
import Icon from "../Common/Icon";
import Label from "../Common/Label";
import LocalDateTimePicker from "../Common/LocalDateTimePicker";
import Tooltip from "../Common/Tooltip";

function dueDateOptions(): Date[] {
    const now = new Date();
    return [
        add(now, { hours: 2 }),
        add(now, { hours: 4 }),
        add(now, { days: 1 }),
        add(now, { days: 2 }),
        add(now, { weeks: 1 }),
    ];
}

function SelectDueDateButton(props: { date: Date; onSelect: () => void }) {
    const { onSelect, date } = props;
    return (
        <Button.Ghost
            onClick={onSelect}
            className="mentions__due-date-button select-due-date__button"
        >
            <FormattedDateTime.RelativeAndAbsoluteDateTime date={date} />
        </Button.Ghost>
    );
}
function DueDateComponent(props: {
    dueDate: Date | undefined;
    onSelect: (dueDate: Date | undefined) => void;
    close: () => void;
}) {
    const { onSelect, close, dueDate } = props;
    const [mode, setMode] = useState<"presets" | "specificDate">("presets");
    return (
        <Flex.Column>
            <Button.Plain onClick={close} className="mentions__due-date-button">
                <Icon.ArrowBack.Small />
                Add a due date
            </Button.Plain>
            {mode === "presets" ? (
                dueDateOptions().map((date) => (
                    <SelectDueDateButton
                        date={date}
                        key={date.getTime()}
                        onSelect={() => {
                            onSelect(date);
                        }}
                    />
                ))
            ) : (
                <LocalDateTimePicker
                    initialDate={dueDate}
                    onConfirm={(d) => {
                        onSelect(d);
                        close();
                    }}
                    onCancel={() => {
                        setMode("presets");
                    }}
                />
            )}
            <Button.Plain
                onClick={() => {
                    setMode("specificDate");
                }}
            >
                <Icon.CalendarMonth.Small /> Select a specific date
            </Button.Plain>
        </Flex.Column>
    );
}

function agentMatchesQuery(agent: Agent, query: string): boolean {
    const term = query.trim().toLowerCase();
    return (
        term.length === 0 ||
        agent.name.toLowerCase().includes(term) ||
        agent.firstName.toLowerCase().includes(term)
    );
}

function Mentions(props: {
    agents: Agent[];
    disabled: boolean;
    dueDate: Date | undefined;
    selectedAgents: Agent[];
    onSelect: (selectedAgents: Agent[], dueDate: Date | undefined) => void;
}) {
    const { agents, disabled, dueDate, selectedAgents, onSelect } = props;
    const [visible, setVisible] = useState<boolean>(false);
    const [mode, setMode] = useState<"mention" | "dueDate">("mention");
    const chipsRef = useComboboxAnchor();

    const isActive = selectedAgents.length !== 0;
    const tooltip = isActive
        ? `${agentNames(selectedAgents)} will be notified`
        : "Add mention";

    const mentions = (
        <>
            <Icon.PersonAdd.Medium className="chat-reply__action-mentions-icon" />

            {isActive && (
                <Flex.Row>
                    <AgentAvatarList agents={selectedAgents} numberOfAvatars={2} />
                    <Label.Small.N50>
                        <FormattedDateTime.PrettyTimestamp date={dueDate} />
                    </Label.Small.N50>
                </Flex.Row>
            )}
        </>
    );

    const mentionsContainer = (
        <Flex.Row
            className={classNames(
                "chat-reply__action-mentions",
                isActive && "chat-reply__action-mentions--active",
            )}
            onClick={() => {
                if (disabled) return;

                setVisible(!visible);
            }}
        >
            {visible ? (
                mentions
            ) : (
                <Tooltip label={tooltip} placement="top">
                    {mentions}
                </Tooltip>
            )}
        </Flex.Row>
    );

    return (
        <div className="mentions">
            {visible && (
                <HandleClicksOutside
                    disabled={!visible}
                    className="mentions__list"
                    onClickOutside={() => {
                        setVisible(false);
                    }}
                >
                    {mode === "mention" ? (
                        <Flex.Column gap="medium">
                            <Combobox
                                multiple
                                defaultOpen
                                autoHighlight
                                items={agents.filter((agent) => !agent.disabled)}
                                value={selectedAgents}
                                onValueChange={(next) => onSelect(next, dueDate)}
                                itemToStringLabel={(agent) => agent.name}
                                isItemEqualToValue={(a, b) => a.id === b.id}
                                filter={agentMatchesQuery}
                            >
                                <ComboboxChips
                                    ref={chipsRef}
                                    className="w-full min-w-0"
                                >
                                    {selectedAgents.map((agent) => (
                                        <ComboboxChip
                                            key={agent.id}
                                            className="min-w-0 max-w-full overflow-hidden"
                                        >
                                            <AgentAvatar
                                                agentId={agent.id}
                                                size="small"
                                                className="shrink-0"
                                            />
                                            <span className="min-w-0 truncate">
                                                {agent.name}
                                            </span>
                                        </ComboboxChip>
                                    ))}
                                    <ComboboxChipsInput
                                        autoFocus
                                        placeholder="Search"
                                        aria-label="Search agents"
                                    />
                                </ComboboxChips>
                                <ComboboxContent
                                    anchor={chipsRef}
                                    side="top"
                                    className="min-w-(--anchor-width) max-w-(--anchor-width)"
                                    positionerClassName="z-[var(--stack-order-level4)]"
                                >
                                    <ComboboxEmpty>No agents found</ComboboxEmpty>
                                    <ComboboxList>
                                        {(agent: Agent) => (
                                            <ComboboxItem key={agent.id} value={agent}>
                                                <AgentAvatar
                                                    agentId={agent.id}
                                                    size="small"
                                                />
                                                <span className="min-w-0 flex-1 truncate">
                                                    {agent.name}
                                                </span>
                                            </ComboboxItem>
                                        )}
                                    </ComboboxList>
                                </ComboboxContent>
                            </Combobox>
                            <Button.Plain
                                onClick={() => {
                                    setMode("dueDate");
                                }}
                                className="mentions__due-date-button"
                                disabled={selectedAgents.length === 0}
                            >
                                <Icon.CalendarMonth.Small />
                                <Label.Medium.Light>
                                    {dueDate ? (
                                        <>
                                            Due{" "}
                                            <FormattedDateTime.Default date={dueDate} />
                                        </>
                                    ) : (
                                        "Add a due date"
                                    )}
                                </Label.Medium.Light>
                            </Button.Plain>
                        </Flex.Column>
                    ) : (
                        <DueDateComponent
                            dueDate={dueDate}
                            close={() => {
                                setMode("mention");
                            }}
                            onSelect={(selectedDueDate) => {
                                onSelect(selectedAgents, selectedDueDate);
                                setMode("mention");
                            }}
                        />
                    )}
                </HandleClicksOutside>
            )}

            {mentionsContainer}
        </div>
    );
}

export default Mentions;
